/* Servicio de Capacitaciones Administrativas (TD-11629).
 * Centraliza las llamadas al backend (prefijo /capacitacion-adm/...).
 * El token de autenticación lo agrega el interceptor de ./api.
 */
import api from "./api";

export type Modalidad = "PRESENCIAL" | "VIRTUAL";
export type EstadoCapacitacion = "BORRADOR" | "ABIERTA" | "CERRADA" | "ANULADA";
export type EstadoParticipante = "PENDIENTE" | "CONFIRMADO" | "RECHAZADO";

export interface OpcionForm {
  id?: number;
  /** Clave local para React (no viaja al backend). */
  _k?: number;
  texto: string;
  es_correcta: boolean;
}

export interface PreguntaForm {
  id?: number;
  /** Clave local para React (no viaja al backend). */
  _k?: number;
  texto: string;
  opciones: OpcionForm[];
}

export interface CapacitacionForm {
  tema: string;
  descripcion: string;
  /** YYYY-MM-DDTHH:MM. Puede ir vacío solo en un borrador. */
  fecha_capacitacion: string | null;
  duracion_minutos: number | null;
  modalidad: Modalidad;
  lugar: string;
  departamentos: string[];
  /** true = publicar (de borrador a ABIERTA); false/ausente = guardar como borrador. */
  publicar?: boolean;
  /** Se omite cuando la evaluación ya empezó (las preguntas quedan fijas). */
  preguntas?: PreguntaForm[];
}

export interface CapacitacionResumen {
  id: number;
  tema: string;
  fecha_capacitacion: string | null;
  duracion_minutos: number | null;
  modalidad: Modalidad;
  lugar: string | null;
  estado: EstadoCapacitacion;
  confirmados: number;
  pendientes: number;
  evaluados: number;
}

export interface Participante {
  cedula: string;
  legajo: number | null;
  nombre: string | null;
  cargo: string | null;
  departamento: string | null;
  metodo: "QR" | "MANUAL";
  estado: EstadoParticipante;
  fecha_registro: string;
  fecha_respuesta: string | null;
  puntaje: number | null;
  fecha_evaluacion: string | null;
}

export interface CapacitacionDetalle {
  id: number;
  tema: string;
  descripcion: string | null;
  fecha_capacitacion: string | null;
  /** fecha + duración: a partir de ahí se habilita la evaluación. */
  fecha_fin: string | null;
  /** Ya terminó (o se cerró el registro): los participantes pueden responder. */
  evaluacion_disponible: boolean;
  duracion_minutos: number | null;
  modalidad: Modalidad;
  lugar: string | null;
  estado: EstadoCapacitacion;
  anfitrion_nombre: string | null;
  departamentos: string[];
  preguntas: PreguntaForm[];
  evaluacion_iniciada: boolean;
  participantes: Participante[];
}

export interface Funcionario {
  cedula: string;
  legajo: number | null;
  nombre: string;
  cargo: string;
  departamento: string;
}

export interface CapacitacionPublica {
  id: number;
  tema: string;
  descripcion?: string | null;
  fecha_capacitacion: string;
  modalidad?: Modalidad;
  lugar?: string | null;
  anfitrion_nombre: string | null;
}

export interface EscaneoQR {
  capacitacion: CapacitacionPublica;
  ya_confirmado: boolean;
  texto_consentimiento: string;
  ticket: string;
}

export interface ConfirmacionPendiente extends CapacitacionPublica {
  texto_consentimiento: string;
}

export interface Pendientes {
  confirmaciones: ConfirmacionPendiente[];
  evaluaciones: CapacitacionPublica[];
}

export interface PreguntaEvaluacion {
  id: number;
  texto: string;
  opciones: Array<{ id: number; texto: string }>;
}

export interface ResultadoEvaluacion {
  tema: string;
  puntaje: number;
  correctas: number;
  total: number;
  detalle: Array<{ pregunta: string; respuesta: string; correcta: string | null; es_correcta: boolean }>;
}

export interface MiCapacitacion {
  id: number;
  tema: string;
  fecha_capacitacion: string;
  modalidad: Modalidad;
  anfitrion_nombre: string | null;
  metodo: "QR" | "MANUAL";
  fecha_respuesta: string | null;
  puntaje: number | null;
  fecha_evaluacion: string | null;
  tiene_evaluacion: boolean;
  evaluacion_disponible: boolean;
  fecha_fin: string | null;
}

/** Respuesta al confirmar asistencia (QR o carga manual). */
export interface ConfirmacionResultado {
  info: string;
  tiene_evaluacion: boolean;
  /** false mientras la capacitación sigue en curso. */
  evaluacion_disponible: boolean;
  fecha_fin: string | null;
}

/** Evento global para que el modal de pendientes vuelva a consultar y se abra
 *  (por ej. después de confirmar asistencia por QR). */
export const CAPADM_ABRIR_PENDIENTES = "capadm:abrir-pendientes";

export const abrirPendientesCapAdm = () => window.dispatchEvent(new Event(CAPADM_ABRIR_PENDIENTES));

/** Lo emite el modal al confirmar/rechazar o enviar una evaluación, para que
 *  "Mis capacitaciones" refresque contadores e historial. */
export const CAPADM_PENDIENTES_CAMBIARON = "capadm:pendientes-cambiaron";

const BASE = "/capacitacion-adm";
/** Caché por sesión del permiso de anfitrión (se borra al cerrar sesión). */
export const CAPADM_ACCESO_KEY = "capadm_es_anfitrion_session";

const CapacitacionAdmAPI = {
  /** Cacheado por sesión: el Menu se remonta en cada navegación. */
  async esAnfitrion(): Promise<boolean> {
    const cache = sessionStorage.getItem(CAPADM_ACCESO_KEY);
    if (cache !== null) return cache === "1";
    try {
      const { data } = await api.get(`${BASE}/acceso`);
      const puede = Boolean(data?.es_anfitrion);
      sessionStorage.setItem(CAPADM_ACCESO_KEY, puede ? "1" : "0");
      return puede;
    } catch {
      return false;
    }
  },

  async departamentos(): Promise<string[]> {
    const { data } = await api.get(`${BASE}/departamentos`);
    return data?.datos || [];
  },

  async buscarFuncionarios(q: string): Promise<Funcionario[]> {
    const { data } = await api.get(`${BASE}/funcionarios`, { params: { q } });
    return data?.datos || [];
  },

  async listar(): Promise<CapacitacionResumen[]> {
    const { data } = await api.get(`${BASE}/anfitrion/capacitaciones`);
    return data?.datos || [];
  },

  async crear(form: CapacitacionForm): Promise<{ id: number; info: string }> {
    const { data } = await api.post(`${BASE}/anfitrion/capacitaciones`, form);
    return { id: data?.id, info: data?.info };
  },

  async obtener(id: number): Promise<CapacitacionDetalle> {
    const { data } = await api.get(`${BASE}/anfitrion/capacitaciones/${id}`);
    return data?.datos;
  },

  async actualizar(id: number, form: CapacitacionForm): Promise<{ info: string }> {
    const { data } = await api.put(`${BASE}/anfitrion/capacitaciones/${id}`, form);
    return data;
  },

  async cambiarEstado(id: number, estado: EstadoCapacitacion): Promise<{ info: string }> {
    const { data } = await api.post(`${BASE}/anfitrion/capacitaciones/${id}/estado`, { estado });
    return data;
  },

  async qr(id: number): Promise<{ qr: string; expira_en: number; ventana_segundos: number }> {
    const { data } = await api.get(`${BASE}/anfitrion/capacitaciones/${id}/qr`);
    return data;
  },

  async agregarParticipantes(
    id: number,
    cedulas: string[]
  ): Promise<{ info: string; agregados: string[]; ya_registrados: string[]; no_encontrados: string[] }> {
    const { data } = await api.post(`${BASE}/anfitrion/capacitaciones/${id}/participantes`, { cedulas });
    return data;
  },

  async quitarParticipante(id: number, cedula: string) {
    const { data } = await api.delete(`${BASE}/anfitrion/capacitaciones/${id}/participantes/${cedula}`);
    return data;
  },

  async recordarPendientes(id: number) {
    const { data } = await api.post(`${BASE}/anfitrion/capacitaciones/${id}/recordar`);
    return data;
  },

  // ---------------- Participante ----------------

  async escanearQR(qr: string): Promise<EscaneoQR> {
    const { data } = await api.post(`${BASE}/asistencia/qr`, { qr });
    return data;
  },

  async confirmarQR(ticket: string): Promise<ConfirmacionResultado> {
    const { data } = await api.post(`${BASE}/asistencia/qr/confirmar`, { ticket });
    return data;
  },

  /** Ante error devuelve null: nunca debe trabar el inicio de la app. */
  async pendientes(): Promise<Pendientes | null> {
    try {
      const { data } = await api.get(`${BASE}/pendientes`);
      return { confirmaciones: data?.confirmaciones || [], evaluaciones: data?.evaluaciones || [] };
    } catch {
      return null;
    }
  },

  async responderPendiente(id: number, participo: boolean): Promise<ConfirmacionResultado> {
    const { data } = await api.post(`${BASE}/pendientes/${id}/responder`, { participo });
    return data;
  },

  async mis(): Promise<MiCapacitacion[]> {
    const { data } = await api.get(`${BASE}/mis`);
    return data?.datos || [];
  },

  async evaluacion(id: number): Promise<{ tema: string; preguntas: PreguntaEvaluacion[] }> {
    const { data } = await api.get(`${BASE}/evaluacion/${id}`);
    return data;
  },

  async enviarEvaluacion(
    id: number,
    respuestas: Array<{ pregunta_id: number; opcion_id: number }>
  ): Promise<ResultadoEvaluacion> {
    const { data } = await api.post(`${BASE}/evaluacion/${id}`, { respuestas });
    return data?.resultado;
  },
};

export default CapacitacionAdmAPI;
